import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import {
  Button, Card, Col, Form, Row, Table, Spinner,
} from "react-bootstrap";
import {
  FaFileExcel, FaMoneyBillWave, FaUniversity, FaLayerGroup,
  FaTools, FaChartPie,
} from "react-icons/fa";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, Cell,
} from "recharts";
import axiosInstance from "../../axiosInstance";
import AdLayout from "../../components/AdLayout";
import ReportSummaryCard from "./components/ReportSummaryCard";
import { PERIOD_OPTIONS, buildPeriodParams, getPeriodLabel } from "./utils/periodFilter";
import { buildChips, countActiveChips, formatDateChip } from "./utils/accountingFilterUtils";
import AccountingFilterSection from "./components/AccountingFilterSection";
import AccountingActiveFilters from "./components/AccountingActiveFilters";
import { formatVND as fmtVND, formatMoney } from "./utils/moneyFormat";
import "./accountingFilters.css";
import "./accountingResponsive.css";

const unwrap = (res) => res?.data?.data ?? res?.data ?? {};
const DEFAULT_PERIOD = "MONTH";
const isCanceled = (err) =>
  err?.code === "ERR_CANCELED" || err?.name === "CanceledError" || err?.name === "AbortError";

const buildDefaultFilters = () => ({
  keyword: "",
  period: DEFAULT_PERIOD,
  ...buildPeriodParams(DEFAULT_PERIOD),
});
const fmtShort = (v) => {
  const n = Number(v || 0);
  const abs = Math.abs(n);
  if (abs >= 1e9) return `${(n / 1e9).toFixed(1)} tỷ`;
  if (abs >= 1e6) return `${(n / 1e6).toFixed(1)} tr`;
  return formatMoney(n);
};

const ASSET_PIE_COLORS = ["#198754", "#20c997", "#fd7e14"];

const AssetReportList = ({ embedded = false }) => {
  const [loading, setLoading] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [chartMetric, setChartMetric] = useState("total"); // total | current | fixed

  const initial = useMemo(() => buildDefaultFilters(), []);
  const [filterKeyword, setFilterKeyword] = useState("");
  const [filterFrom, setFilterFrom] = useState(initial.dateFrom || "");
  const [filterTo, setFilterTo] = useState(initial.dateTo || "");
  const [period, setPeriod] = useState(DEFAULT_PERIOD);
  const [appliedFilters, setAppliedFilters] = useState(initial);
  const [report, setReport] = useState(null);

  const fetchSeqRef = useRef(0);
  const abortRef = useRef(null);
  const filtersRef = useRef(appliedFilters);
  filtersRef.current = appliedFilters;

  const chipDefs = useMemo(() => [
    {
      key: "period",
      label: "Thời gian",
      isActive: (v) => v && v !== DEFAULT_PERIOD,
      formatValue: (v, applied) => (
        v === "CUSTOM"
          ? formatDateChip(applied.dateFrom, applied.dateTo)
          : PERIOD_OPTIONS.find((o) => o.value === v)?.label || v
      ),
    },
    {
      key: "keyword",
      label: "Từ khóa",
      isActive: (v) => Boolean(v),
      formatValue: (v) => v,
    },
  ], []);

  const activeChips = useMemo(
    () => buildChips(appliedFilters, chipDefs),
    [appliedFilters, chipDefs]
  );

  const fetchData = useCallback(async (filters) => {
    const next = filters || filtersRef.current;
    const seq = ++fetchSeqRef.current;
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    try {
      const params = { period: next.period || DEFAULT_PERIOD };
      if (next.dateFrom) params.dateFrom = next.dateFrom;
      if (next.dateTo) params.dateTo = next.dateTo;
      if (next.keyword) params.keyword = next.keyword;
      const res = await axiosInstance.get("/api/accounting/assets/report", {
        params,
        signal: controller.signal,
      });
      if (seq !== fetchSeqRef.current) return;
      setReport(unwrap(res));
    } catch (err) {
      if (isCanceled(err) || seq !== fetchSeqRef.current) return;
      toast.error("Không thể tải báo cáo tài sản");
    } finally {
      if (seq === fetchSeqRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const defaults = buildDefaultFilters();
    setAppliedFilters(defaults);
    setPeriod(defaults.period);
    setFilterFrom(defaults.dateFrom || "");
    setFilterTo(defaults.dateTo || "");
    fetchData(defaults);
    return () => { if (abortRef.current) abortRef.current.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyFilters = (next) => {
    setAppliedFilters(next);
    filtersRef.current = next;
    setReport(null);
    fetchData(next);
  };

  const syncDraft = (filters) => {
    setFilterKeyword(filters.keyword || "");
    setPeriod(filters.period || DEFAULT_PERIOD);
    setFilterFrom(filters.dateFrom || "");
    setFilterTo(filters.dateTo || "");
  };

  const buildNextFilters = () => {
    const range = period === "CUSTOM"
      ? { dateFrom: filterFrom, dateTo: filterTo }
      : buildPeriodParams(period);
    return {
      keyword: filterKeyword.trim(),
      period,
      dateFrom: range.dateFrom || "",
      dateTo: range.dateTo || "",
    };
  };

  const handleSearch = () => applyFilters(buildNextFilters());
  const handleReset = () => {
    const defaults = buildDefaultFilters();
    syncDraft(defaults);
    applyFilters(defaults);
  };
  const handleRemoveChip = (key) => {
    const base = { ...appliedFilters };
    if (key === "period") {
      const defaults = buildDefaultFilters();
      Object.assign(base, {
        period: defaults.period,
        dateFrom: defaults.dateFrom,
        dateTo: defaults.dateTo,
      });
    } else {
      base[key] = "";
    }
    syncDraft(base);
    applyFilters(base);
  };

  const exportExcel = () => {
    if (!report) { toast.warn("Không có dữ liệu để xuất"); return; }
    const items = report.items || [];
    const wb = XLSX.utils.book_new();
    const summarySheet = XLSX.utils.json_to_sheet([
      { Chỉ_số: "Tiền mặt", Giá_trị: report.cashFund || 0 },
      { Chỉ_số: "Tiền ngân hàng", Giá_trị: report.bankDeposits || 0 },
      { Chỉ_số: "TS lưu động", Giá_trị: report.currentAssets || 0 },
      { Chỉ_số: "TS cố định (còn lại)", Giá_trị: report.fixedAssets ?? report.ccdcBookValue ?? 0 },
      { Chỉ_số: "Tổng tài sản", Giá_trị: report.totalAssets || 0 },
      { Chỉ_số: "As of", Giá_trị: report.asOf || "" },
    ]);
    XLSX.utils.book_append_sheet(wb, summarySheet, "TomTat");

    const fundSheet = XLSX.utils.json_to_sheet([
      { Loại: "Tiền mặt (CASH)", Số_dư: report.cashFund || 0 },
      { Loại: "Tiền ngân hàng (BANK)", Số_dư: report.bankDeposits || 0 },
      { Loại: "Tổng lưu động", Số_dư: report.currentAssets || 0 },
    ]);
    XLSX.utils.book_append_sheet(wb, fundSheet, "Quy");

    const detailSheet = XLSX.utils.json_to_sheet(
      items.map((r, i) => ({
        STT: i + 1,
        Mã: r.code || "",
        Tên: r.name || "",
        "Nguyên giá": r.amount || 0,
        "KH (tháng)": r.depreciationMonths || 0,
        "Ngày bắt đầu": r.expenseDate || "",
        "Đã KH (lũy kế)": r.amortized || 0,
        "Còn lại": r.bookValue || 0,
        "% còn lại": r.remainingPct != null ? Number(r.remainingPct) : 0,
        "Chi/tháng": r.monthlyAmount || 0,
      }))
    );
    XLSX.utils.book_append_sheet(wb, detailSheet, "ChiTietCPT");
    XLSX.writeFile(wb, "BaoCaoTaiSan.xlsx");
  };

  const periodLabel = getPeriodLabel(appliedFilters.period);
  const rangeHint = appliedFilters.dateFrom && appliedFilters.dateTo
    ? `${appliedFilters.dateFrom} → ${appliedFilters.dateTo}`
    : "";
  const items = report?.items || [];
  const trend = Array.isArray(report?.trend) ? report.trend : [];

  const chartData = useMemo(() => trend.map((d) => ({
    bucket: d.bucket,
    cashFund: Number(d.cashFund || 0),
    bankDeposits: Number(d.bankDeposits || 0),
    currentAssets: Number(d.currentAssets || 0),
    fixedAssets: Number(d.fixedAssets || 0),
    totalAssets: Number(d.totalAssets || d.current || 0),
  })), [trend]);

  const pieAllocation = useMemo(() => {
    const slices = [
      { name: "Tiền mặt", value: Number(report?.cashFund || 0), color: ASSET_PIE_COLORS[0] },
      { name: "Tiền ngân hàng", value: Number(report?.bankDeposits || 0), color: ASSET_PIE_COLORS[1] },
      { name: "TS cố định", value: Number(report?.fixedAssets ?? report?.ccdcBookValue ?? 0), color: ASSET_PIE_COLORS[2] },
    ].filter((s) => s.value > 0);
    const sum = slices.reduce((acc, s) => acc + s.value, 0);
    return slices.map((s) => ({
      ...s,
      sharePct: sum > 0 ? (s.value / sum) * 100 : 0,
    }));
  }, [report]);

  const chartTitle = chartMetric === "current"
    ? `Biến động tài sản lưu động — ${periodLabel}`
    : chartMetric === "fixed"
      ? `Biến động TS cố định (CPT còn lại) — ${periodLabel}`
      : `Biến động tổng tài sản — ${periodLabel}`;

  const pieSliceLabel = ({ percent }) =>
    `${((Number(percent) || 0) * 100).toFixed(0)}%`;

  const pieLegendFormatter = (value, entry) => {
    const pct = entry?.payload?.sharePct;
    if (pct == null || Number.isNaN(Number(pct))) return value;
    return `${value} (${Number(pct).toFixed(1)}%)`;
  };

  const pieTooltipFormatter = (v, _n, item) => {
    const pct = item?.payload?.sharePct;
    const pctText = pct != null ? ` (${Number(pct).toFixed(1)}%)` : "";
    return [`${fmtVND(v)}${pctText}`, item?.payload?.name || ""];
  };

  const body = (
    <div className={embedded ? "" : "acc-page"}>
      <Card className="mb-3 acc-filter-card">
        <Card.Body className="p-0">
          <AccountingFilterSection
            title={!embedded ? "Báo cáo Tài sản" : undefined}
            open={filterOpen}
            onToggle={setFilterOpen}
            onSubmit={handleSearch}
            onReset={handleReset}
            activeCount={countActiveChips(activeChips)}
            toolbarRight={(
              <Button variant="success" size="sm" onClick={exportExcel} disabled={loading || !report}>
                <FaFileExcel className="me-1" /> Xuất Excel
              </Button>
            )}
          >
            <Row className="g-2 align-items-end acc-filter-grid">
              <Col>
                <Form.Label className="small mb-1">Thời gian</Form.Label>
                <Form.Select size="sm" value={period} onChange={(e) => setPeriod(e.target.value)}>
                  {PERIOD_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </Form.Select>
              </Col>
              {period === "CUSTOM" && (
                <>
                  <Col>
                    <Form.Label className="small mb-1">Từ ngày</Form.Label>
                    <Form.Control type="date" size="sm" value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)} />
                  </Col>
                  <Col>
                    <Form.Label className="small mb-1">Đến ngày</Form.Label>
                    <Form.Control type="date" size="sm" value={filterTo} onChange={(e) => setFilterTo(e.target.value)} />
                  </Col>
                </>
              )}
              <Col>
                <Form.Label className="small mb-1">Tìm mã / tên CPT</Form.Label>
                <Form.Control
                  size="sm"
                  placeholder="Mã hoặc tên…"
                  value={filterKeyword}
                  onChange={(e) => setFilterKeyword(e.target.value)}
                />
              </Col>
            </Row>
          </AccountingFilterSection>
          <AccountingActiveFilters chips={activeChips} onRemove={handleRemoveChip} onClearAll={handleReset} />
        </Card.Body>
      </Card>

      {rangeHint && (
        <div className="text-muted small mb-2">
          Kỳ báo cáo: <strong>{periodLabel}</strong> ({rangeHint})
        </div>
      )}

      <div className="acc-summary-grid--equal">
        <div className="acc-summary-card-wrap">
          <ReportSummaryCard
            title="Tổng tài sản"
            value={report?.totalAssets}
            color="#6f42c1"
            icon={FaChartPie}
            loading={loading}
            compact
            subtitle="Lưu động + cố định"
            onClick={() => setChartMetric("total")}
            active={chartMetric === "total"}
          />
        </div>
        <div className="acc-summary-card-wrap">
          <ReportSummaryCard title="Tiền mặt" value={report?.cashFund} color="#198754" icon={FaMoneyBillWave} loading={loading} compact />
        </div>
        <div className="acc-summary-card-wrap">
          <ReportSummaryCard title="Tiền ngân hàng" value={report?.bankDeposits} color="#20c997" icon={FaUniversity} loading={loading} compact />
        </div>
        <div className="acc-summary-card-wrap">
          <ReportSummaryCard
            title="TS lưu động"
            value={report?.currentAssets}
            color="#0d6efd"
            icon={FaLayerGroup}
            loading={loading}
            compact
            subtitle="TM + Tiền ngân hàng"
            onClick={() => setChartMetric("current")}
            active={chartMetric === "current"}
          />
        </div>
        <div className="acc-summary-card-wrap">
          <ReportSummaryCard
            title="TS cố định (còn lại)"
            value={report?.fixedAssets ?? report?.ccdcBookValue}
            color="#fd7e14"
            icon={FaTools}
            loading={loading}
            compact
            subtitle="CPT sau KH lũy kế"
            onClick={() => setChartMetric("fixed")}
            active={chartMetric === "fixed"}
          />
        </div>
      </div>

      {loading ? (
        <Card className="mb-3"><Card.Body className="text-center py-4"><Spinner animation="border" size="sm" className="me-2" />Đang tải…</Card.Body></Card>
      ) : chartData.length === 0 && pieAllocation.length === 0 ? (
        <Card className="mb-3"><Card.Body className="text-center text-muted py-4">Không có dữ liệu biểu đồ</Card.Body></Card>
      ) : (
        <Row className="g-3 mb-3">
          <Col md={8}>
            {chartData.length === 0 ? (
              <Card className="h-100" style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
                <Card.Body className="text-center text-muted py-4">Không có dữ liệu biến động</Card.Body>
              </Card>
            ) : (
              <Card className="h-100" style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
                <Card.Body>
                  <h6 className="fw-semibold mb-3">{chartTitle}</h6>
                  <div className="acc-chart-wrap">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="bucket" tick={{ fontSize: 12 }} />
                        <YAxis tickFormatter={fmtShort} tick={{ fontSize: 12 }} width={70} />
                        <Tooltip formatter={(v) => fmtVND(v)} />
                        <Legend />
                        {chartMetric === "total" && (
                          <>
                            <Line type="monotone" dataKey="cashFund" name="Tiền mặt" stroke="#198754" strokeWidth={2} dot={{ r: 2 }} />
                            <Line type="monotone" dataKey="bankDeposits" name="Tiền ngân hàng" stroke="#20c997" strokeWidth={2} dot={{ r: 2 }} />
                            <Line type="monotone" dataKey="fixedAssets" name="TS cố định" stroke="#fd7e14" strokeWidth={2} dot={{ r: 2 }} />
                            <Line type="monotone" dataKey="totalAssets" name="Tổng" stroke="#6f42c1" strokeWidth={2.5} dot={{ r: 3 }} />
                          </>
                        )}
                        {chartMetric === "current" && (
                          <>
                            <Line type="monotone" dataKey="cashFund" name="Tiền mặt" stroke="#198754" strokeWidth={2} dot={{ r: 2 }} />
                            <Line type="monotone" dataKey="bankDeposits" name="Tiền ngân hàng" stroke="#20c997" strokeWidth={2} dot={{ r: 2 }} />
                            <Line type="monotone" dataKey="currentAssets" name="TS lưu động" stroke="#0d6efd" strokeWidth={2.5} dot={{ r: 3 }} />
                          </>
                        )}
                        {chartMetric === "fixed" && (
                          <Line type="monotone" dataKey="fixedAssets" name="TS cố định (còn lại)" stroke="#fd7e14" strokeWidth={2.5} dot={{ r: 3 }} />
                        )}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </Card.Body>
              </Card>
            )}
          </Col>
          <Col md={4}>
            <Card className="h-100" style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
              <Card.Body>
                <h6 className="fw-semibold mb-3">Phân bổ tài sản</h6>
                {pieAllocation.length > 0 ? (
                  <div className="acc-chart-wrap">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
                      <Pie
                        data={pieAllocation}
                        cx="50%"
                        cy="46%"
                        innerRadius={48}
                        outerRadius={78}
                        paddingAngle={2}
                        dataKey="value"
                        nameKey="name"
                        label={pieSliceLabel}
                        labelLine={{ strokeWidth: 1 }}
                      >
                        {pieAllocation.map((s, i) => (
                          <Cell key={`asset-pie-${i}`} fill={s.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={pieTooltipFormatter} />
                      <Legend
                        verticalAlign="bottom"
                        formatter={pieLegendFormatter}
                        wrapperStyle={{ paddingTop: 8, fontSize: 12 }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="text-center text-muted py-5">Không có dữ liệu</div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      <Card style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
        <Card.Body className="p-0">
          <div className="px-3 pt-3 pb-1">
            <h6 className="fw-semibold mb-0">Chi phí trả trước / tài sản đang theo dõi</h6>
          </div>
          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
          ) : items.length === 0 ? (
            <div className="text-center py-5 text-muted">Không có khoản chi phí trả trước trong kỳ lọc</div>
          ) : (
            <div className="acc-table-wrap">
              <Table striped hover className="mb-0 align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Mã</th>
                    <th>Tên</th>
                    <th className="text-end">Nguyên giá</th>
                    <th className="text-center acc-col-hide-sm">KH (tháng)</th>
                    <th className="acc-col-hide-sm">Ngày BĐ</th>
                    <th className="text-end acc-col-hide-sm">Đã KH (lũy kế)</th>
                    <th className="text-end">Còn lại</th>
                    <th className="text-center acc-col-hide-sm">% còn</th>
                    <th className="text-end acc-col-hide-sm">Chi/tháng</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((r) => (
                    <tr key={r.id}>
                      <td className="fw-semibold">{r.code || "—"}</td>
                      <td>{r.name || "—"}</td>
                      <td className="text-end">{fmtVND(r.amount)}</td>
                      <td className="text-center acc-col-hide-sm">{r.depreciationMonths ?? "—"}</td>
                      <td className="acc-col-hide-sm">{r.expenseDate || "—"}</td>
                      <td className="text-end acc-col-hide-sm text-muted">{fmtVND(r.amortized)}</td>
                      <td className="text-end fw-semibold text-primary">{fmtVND(r.bookValue)}</td>
                      <td className="text-center acc-col-hide-sm">{r.remainingPct != null ? `${r.remainingPct}%` : "—"}</td>
                      <td className="text-end acc-col-hide-sm">{fmtVND(r.monthlyAmount)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );

  if (embedded) return body;
  return <AdLayout>{body}</AdLayout>;
};

export default AssetReportList;
